/**
 * A small horizontal pill group with mutually-exclusive options. Designed for
 * 2–4 short labels like "Wood / Stone" or "Light / Dark".
 *
 * Used by ThemePicker for the table's material × tone pickers and (Phase 3)
 * the piece's material / size pickers.
 */
export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Optional inline icon (a Lucide component) shown before the label. */
  readonly icon?: React.ComponentType<{ className?: string; size?: number | string }>;
}

export interface SegmentedProps<T extends string> {
  readonly options: ReadonlyArray<SegmentedOption<T>>;
  readonly value: T;
  readonly onChange: (next: T) => void;
  /** Stable id used in `data-testid="<testid>-<value>"`. */
  readonly testid: string;
  readonly ariaLabel: string;
  readonly className?: string;
  /**
   * The group is off limits — a lock or a preset decides this setting. The options stay visible
   * (so the child can see what it would be) but they do not respond.
   */
  readonly disabled?: boolean;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  testid,
  ariaLabel,
  className = '',
  disabled = false,
}: SegmentedProps<T>) {
  return (
    <div
      className={`inline-flex flex-wrap gap-1 rounded-full bg-sun/10 p-1 ${className}`}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testid}
      aria-disabled={disabled ? 'true' : undefined}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            data-testid={`${testid}-${opt.value}`}
            onClick={() => {
              if (disabled || selected) return;
              onChange(opt.value);
            }}
            className={`inline-flex min-h-[48px] items-center gap-1 rounded-full px-4 py-1 text-base font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              selected
                ? 'bg-accent text-accent-fg shadow-sm'
                : 'text-ink hover:bg-sun/20'
            }`}
          >
            {Icon ? <Icon size={14} /> : null}
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
